/**
 * Security Type Classification Utilities
 * 
 * Provides intelligent classification of securities into the following types:
 * - Stock
 * - Bond
 * - ETF
 * - Money Market Fund
 * - Long Call
 * - Long Put
 * - Short Call
 * - Short Put
 * - REIT
 * - Mutual Fund
 * - Unknown
 * 
 * Also includes money market fund detection utilities.
 */

import { SecurityType } from '../enums/SecurityType.js';
import { TransactionType } from '../enums/TransactionType.js';
import { AssetClass, getAssetClass, isMoneyMarketSymbol as isCuratedMoneyMarketSymbol, isReitSymbol } from '../config/symbols/index.js';
import { parseOptionSymbol } from './optionSymbol.js';

export const SECURITY_TYPES: SecurityType[] = [
  SecurityType.Stock,
  SecurityType.Bond,
  SecurityType.ETF,
  SecurityType.MoneyMarketFund,
  SecurityType.LongCall,
  SecurityType.LongPut,
  SecurityType.ShortCall,
  SecurityType.ShortPut,
  SecurityType.REIT,
  SecurityType.MutualFund,
  SecurityType.Cash,
];

/**
 * Classify a security based on its symbol and description
 * @param symbol The security symbol
 * @param description Optional description from broker
 * @param transactions Optional array of transactions for this symbol to help classify options
 * @param quantity Optional quantity - negative indicates short position
 */
export function classifySecurityType(
  symbol: string,
  description?: string,
  transactions?: Array<{ action?: string; type?: string; quantity?: number }>,
  quantity?: number
): SecurityType {
  // If no symbol, check description for option indicators
  if (!symbol) {
    const upperDesc = (description || '').toUpperCase();
    // Check for explicit option indicators in description using word boundaries
    // \b matches word boundaries, making this more robust than whitespace checks
    if (/\b(CALL|PUT|OPTION)\b/.test(upperDesc) || /\bEXP\s+\d/.test(upperDesc)) {
      return SecurityType.Option;
    }
    return SecurityType.Unknown;
  }

  const upperSymbol = symbol.toUpperCase().trim();
  const upperDesc = (description || '').toUpperCase();

  // Treasury/Bond detection (T-Bills, T-Notes, T-Bonds)
  if (upperSymbol.startsWith('TBILL') || upperSymbol.startsWith('TNOTE') || upperSymbol.startsWith('TBOND')) {
    return SecurityType.Bond;
  }
  if (upperDesc.includes('TREASURY BILL') || upperDesc.includes('TREASURY NOTE') || upperDesc.includes('TREASURY BOND')) {
    return SecurityType.Bond;
  }

  // Bond detection via description patterns
  // Bonds typically have "DUE" + date (maturity) in description, e.g., "CALIFORNIA HEALTH F 5% DUE 11/15/25"
  // They may also have coupon rates like "5%" or "5.25%"
  const hasDueDate = /\bDUE\s+\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(upperDesc);
  const hasCouponRate = /\b\d+(\.\d+)?%/.test(upperDesc);
  
  // CUSIP detection (9-character alphanumeric identifiers)
  // CUSIPs are exactly 9 characters, contain digits, and are alphanumeric
  // BUT: CUSIPs alone aren't enough - stocks can also have CUSIP symbols in some contexts
  // Only classify as bond if CUSIP + bond-like description patterns
  const isCusip = upperSymbol.length === 9 &&
                  /^[A-Z0-9]{9}$/.test(upperSymbol) &&
                  /\d/.test(upperSymbol);
  
  // Strong bond indicators: CUSIP + (DUE date or coupon rate)
  if (isCusip && (hasDueDate || hasCouponRate)) {
    return SecurityType.Bond;
  }

  // Non-CUSIP DUE-date securities are classified further down (see the
  // maturity-date check below), which excludes ETFs/funds — a target-maturity
  // bond ETF also has "DUE MM/DD/YY" in its description but trades at NAV,
  // not as a percentage of par like an individual bond.

  // Money Market Fund detection — delegates to the curated symbol/keyword
  // lists in config/symbols/moneyMarket.ts (XX-suffix, known symbols, and
  // description keywords including "settlement fund", the exact Vanguard
  // quirk this project otherwise fights: Vanguard reports settlement-fund
  // cash as plain cash with no dedicated holding).
  if (isCuratedMoneyMarketSymbol(upperSymbol, upperDesc)) {
    return SecurityType.MoneyMarketFund;
  }

  // Options detection - use parseOptionSymbol for reliable parsing of all formats
  // Supports: OCC format (AAPL250124C00150000), Vanguard (AVXL 250919 P 9.00),
  // Schwab (AVXL 09/19/2025 9.00 P), standardized (AVXL 2025-09-19 9.00 P)
  const optionParts = parseOptionSymbol(upperSymbol);

  // For descriptions, check if they indicate an option
  const descriptionIndicatesCall = (upperDesc.includes('CALL OPTION') || upperDesc.includes('CALL CONTRACT'));
  const descriptionIndicatesPut = (upperDesc.includes('PUT OPTION') || upperDesc.includes('PUT CONTRACT'));

  // Determine if this is a call or put
  let isCallOption = false;
  let isPutOption = false;

  if (optionParts) {
    // Symbol is a valid option format - use parsed type
    isCallOption = optionParts.type === 'C';
    isPutOption = optionParts.type === 'P';
  } else if (descriptionIndicatesCall) {
    isCallOption = true;
  } else if (descriptionIndicatesPut) {
    isPutOption = true;
  }

  if (isCallOption || isPutOption) {
    // For options, determine if Long or Short based on:
    // 1. Description keywords
    // 2. Quantity sign (negative = short)
    // 3. Transaction history
    let isShort = false;
    let directionDetermined = false;

    // Check description first for explicit indicators
    // Whole phrases only: a bare SHORT/LONG substring also matches fund names
    // such as "VANGUARD SHORT-TERM TREASURY ETF" (SYNC-18).
    if (/\bSHORT (PUT|CALL)\b/.test(upperDesc) || upperDesc.includes('SOLD TO OPEN') || upperDesc.includes('SELL TO OPEN')) {
      isShort = true;
      directionDetermined = true;
    } else if (/\bLONG (PUT|CALL)\b/.test(upperDesc) || upperDesc.includes('BOUGHT TO OPEN') || upperDesc.includes('BUY TO OPEN')) {
      isShort = false;
      directionDetermined = true;
    }

    // Check quantity sign - negative quantity indicates short position
    if (!directionDetermined && quantity !== undefined && quantity !== 0) {
      isShort = quantity < 0;
      directionDetermined = true;
    }

    // Fall back to transaction history
    if (!directionDetermined && transactions && transactions.length > 0) {
      // Check transaction history to determine which side the option belongs to.
      for (const tx of transactions) {
        const action = (tx.action || tx.type || '').toLowerCase();
        if (
          action.includes('sell to open') ||
          action === 'option_open_sell' ||
          action === TransactionType.SellToOpen ||
          action === TransactionType.BuyToClose ||
          action === 'option_close_buy'
        ) {
          isShort = true;
          break;
        } else if (
          action.includes('buy to open') ||
          action === 'option_open_buy' ||
          action === TransactionType.BuyToOpen ||
          action === TransactionType.SellToClose ||
          action === 'option_close_sell'
        ) {
          isShort = false;
          break;
        }
      }
    }

    // Return the appropriate option type
    if (isCallOption) {
      return isShort ? SecurityType.ShortCall : SecurityType.LongCall;
    } else {
      return isShort ? SecurityType.ShortPut : SecurityType.LongPut;
    }
  }

  // REIT detection (check before ETF because some REITs are also ETFs).
  // Checks the curated REIT symbol list first (config/symbols/reit.ts) —
  // it recognizes individual REIT stocks by ticker (e.g. O, AMT, PLD), which
  // this file's description-only keyword list could never catch since those
  // tickers' broker descriptions don't necessarily contain the word "REIT".
  if (isReitSymbol(upperSymbol, upperDesc)) {
    return SecurityType.REIT;
  }
  const reitKeywords = [
    'REIT',
    'REAL ESTATE INVESTMENT TRUST',
    'REAL ESTATE INV TR',
    'REAL ESTATE INDEX',
    'PROPERTIES TR',
    'REALTY TRUST',
    'PROPERTY TR'
  ];
  for (const keyword of reitKeywords) {
    if (upperDesc.includes(keyword)) {
      return SecurityType.REIT;
    }
  }

  // ETF detection — checks the curated asset-class lists in
  // config/symbols/etfClassifications.ts (dozens of symbols across US/
  // international equity, bonds, real estate, commodities, and crypto)
  // before falling back to this file's smaller, description-only keyword
  // list for ETFs the curated data doesn't yet know about.
  if (getAssetClass(upperSymbol) !== AssetClass.Other) {
    return SecurityType.ETF;
  }

  const etfKeywords = [
    'ETF',
    'EXCHANGE TRADED',
    'INDEX FUND',
    'ISHARES',
    'SPDR',
    'VANGUARD ETF',
    'INVESCO',
    'PROSHARES'
  ];

  for (const keyword of etfKeywords) {
    if (upperDesc.includes(keyword)) {
      return SecurityType.ETF;
    }
  }

  // Bond detection - exclude ETFs and mutual funds first
  // Bond ETFs and bond mutual funds trade at NAV/market price, not as percentage of par

  // Check for maturity date pattern first (very strong signal)
  // Pattern: "DUE MM/DD/YY" or "DUE MM-DD-YYYY"
  if (/\bDUE\s+\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/.test(upperDesc)) {
    // Exclude ETFs/funds even if they have DUE in description
    if (!upperDesc.includes('ETF') && !upperDesc.includes('FUND') && !upperDesc.includes('INDEX')) {
      return SecurityType.Bond;
    }
  }

  const bondKeywords = [
    'BOND',
    'TREASURY',
    'CORPORATE BOND',
    'MUNICIPAL BOND',
    'GOVT BOND',
    'NOTES',
    'DEBENTURE'
  ];
  for (const keyword of bondKeywords) {
    if (upperDesc.includes(keyword)) {
      // Exclude if description contains ETF/fund indicators
      if (upperDesc.includes('ETF') || upperDesc.includes('FUND') || upperDesc.includes('INDEX')) {
        // This is likely a bond ETF or bond fund, not an actual bond
        // Fall through to default classification
        break;
      }
      return SecurityType.Bond;
    }
  }

  // Default to Stock for anything else with a regular ticker symbol pattern
  // Typical stocks are 1-5 characters, all letters
  if (/^[A-Z]{1,5}$/.test(upperSymbol)) {
    return SecurityType.Stock;
  }

  return SecurityType.Unknown;
}

/**
 * Get a user-friendly display label for a security type
 */
export function getSecurityTypeLabel(type: SecurityType | undefined): string {
  if (!type) return 'Unknown';

  switch (type) {
    case SecurityType.Stock:
      return 'Stock';
    case SecurityType.Bond:
      return 'Bond';
    case SecurityType.ETF:
      return 'ETF';
    case SecurityType.MoneyMarketFund:
      return 'Money Market Fund';
    case SecurityType.LongCall:
      return 'Long Call';
    case SecurityType.LongPut:
      return 'Long Put';
    case SecurityType.ShortCall:
      return 'Short Call';
    case SecurityType.ShortPut:
      return 'Short Put';
    case SecurityType.Option:
      return 'Option';
    case SecurityType.REIT:
      return 'REIT';
    case SecurityType.MutualFund:
      return 'Mutual Fund';
    case SecurityType.Cash:
      return 'Cash';
    case SecurityType.Unknown:
      return 'Unknown';
    default:
      return type || 'Unknown';
  }
}

/**
 * Check if a security type is an option
 */
export function isOptionType(type: SecurityType | undefined): boolean {
  return type === SecurityType.Option || type === SecurityType.LongCall || type === SecurityType.LongPut || type === SecurityType.ShortCall || type === SecurityType.ShortPut;
}

/**
 * Check if a security type is a call option (Long Call or Short Call)
 */
export function isCallOptionType(type: SecurityType | undefined): boolean {
  return type === SecurityType.LongCall || type === SecurityType.ShortCall;
}

/**
 * Check if a security type is a put option (Long Put or Short Put)
 */
export function isPutOptionType(type: SecurityType | undefined): boolean {
  return type === SecurityType.LongPut || type === SecurityType.ShortPut;
}

/**
 * Check if a security type has no capital gains (like Money Market Funds)
 */
export function hasNoCapitalGains(type: SecurityType | undefined): boolean {
  return type === SecurityType.MoneyMarketFund;
}

/**
 * Detect if a holding is likely a money market fund
 */
export function isMoneyMarketFund(symbol: string, description?: string): boolean {
  if (!symbol) return false;

  const upperSymbol = symbol.toUpperCase().trim();
  const upperDesc = (description || '').toUpperCase();

  // Delegates to the curated symbol/keyword lists in
  // config/symbols/moneyMarket.ts — see the comment in classifySecurityType
  // above for why (this function's own inline keyword list was missing
  // "settlement fund", despite settlementFundService.ts depending on this
  // very function to detect settlement funds).
  if (isCuratedMoneyMarketSymbol(upperSymbol, upperDesc)) {
    return true;
  }

  // Pattern 3: Known treasury/money fund patterns
  if (upperDesc.includes('TREASURY') && upperDesc.includes('MONEY')) {
    return true;
  }

  return false;
}

/**
 * Get a user-friendly label for money market funds
 */
export function getMoneyMarketLabel(): string {
  return 'Money Market Fund';
}

/**
 * Check if a price is consistent with MMF ($1.00)
 */
export function isMoneyMarketPrice(price: number): boolean {
  return Math.abs(price - 1.0) < 0.01; // Within 1 cent of $1.00
}

/**
 * Check if a security type represents a bond.
 * Use this instead of re-classifying - the securityType should already be set at import time.
 */
export function isBondType(securityType: SecurityType | string | undefined | null): boolean {
  if (!securityType) return false;
  return securityType === SecurityType.Bond;
}

/**
 * Check if a security type is explicitly NOT a bond (ETF, Stock, Mutual Fund, etc.)
 * These security types trade at market price, not as percentage of par.
 */
export function isNonBondType(securityType: SecurityType | string | undefined | null): boolean {
  if (!securityType) return false;
  return (
    securityType === SecurityType.ETF ||
    securityType === SecurityType.Stock ||
    securityType === SecurityType.MutualFund ||
    securityType === SecurityType.MoneyMarketFund ||
    securityType === SecurityType.REIT
  );
}
